/**
 * KpiTile.tsx — single KPI tile (label · big value · optional sub line / flag).
 *
 * Re-uses the `.kpi` class from design.css so visual weight matches the TNSC
 * reference. The tile is *not* clickable by default — pass `onClick` if you
 * want it to drill in.
 */

import React from 'react';

import StatusPill, { type StatusPillVariant } from './StatusPill';
import type { Flag } from '../type/api';

interface Props {
	label: string;
	value: React.ReactNode;
	unit?: string;
	sub?: React.ReactNode;
	flag?: Flag;
	flagVariant?: StatusPillVariant;
	flagLabel?: string;
	primary?: boolean;
	onClick?: () => void;
}

export default function KpiTile({
	label,
	value,
	unit,
	sub,
	flag,
	flagVariant = 'submission',
	flagLabel,
	primary = false,
	onClick,
}: Props) {
	const className = `card kpi${primary ? ' primary' : ''}`;
	return (
		<div
			className={className}
			role={onClick ? 'button' : undefined}
			tabIndex={onClick ? 0 : undefined}
			onClick={onClick}
			onKeyDown={
				onClick
					? (e) => {
							if (e.key === 'Enter' || e.key === ' ') {
								e.preventDefault();
								onClick();
							}
						}
					: undefined
			}
			style={onClick ? undefined : { cursor: 'default' }}
		>
			<span className="kpi-accent" aria-hidden="true" />
			<div className="kpi-label">{label}</div>
			<div className="kpi-value">
				<span>{value}</span>
				{unit && <span className="unit">{unit}</span>}
			</div>
			<div className="kpi-foot">
				<div style={{ fontSize: 12, color: 'var(--text-faint)' }}>{sub ?? ''}</div>
				{flag && <StatusPill flag={flag} variant={flagVariant} compact label={flagLabel} />}
			</div>
		</div>
	);
}
